// src/components/ProfileAction.js
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./ProfileAction.css";

/**
 * Drei-Punkte-Menü für den Profilbereich.
 *
 * Aktuell:
 * - Blockierte Nutzer verwalten
 * - Passwort ändern
 * - Feedback-Hinweis
 *
 * Später erweiterbar:
 * - E-Mail ändern
 * - Account löschen
 * - Datenschutzoptionen
 */
function ProfileAction({ className = "", onActionToast = null }) {
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const wrapperRef = useRef(null);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const showToast = (text) => {
    if (typeof onActionToast === "function") {
      onActionToast(text);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setShowFeedbackModal(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // ✅ Scroll-Lock wie beim ContentActionMenu
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;

    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;
    const previousBodyWidth = document.body.style.width;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      document.body.style.width = previousBodyWidth;
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;

      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const handleToggleMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsOpen((prev) => !prev);
  };

  const handleNavigate = (target) => {
    closeMenu();

    setTimeout(() => {
      navigate(target);
    }, 0);
  };

  const handleFeedbackClick = () => {
    closeMenu();

    setTimeout(() => {
      setShowFeedbackModal(true);
    }, 0);
  };

  const menuLayer =
    isOpen &&
    createPortal(
      <>
        <div
          className="profile-action-backdrop"
          onClick={(event) => {
            event.stopPropagation();
            closeMenu();
          }}
          onTouchMove={(event) => {
            event.preventDefault();
          }}
        />

        <div
          className="profile-action-sheet"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleNavigate("/profil/optionen");
            }}
          >
            Blockierte Nutzer verwalten
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleNavigate("/forgot-password");
            }}
          >
            Passwort ändern
          </button>

          <button
  type="button"
  className="danger"
  onClick={(event) => {
    event.preventDefault();
    event.stopPropagation();
    handleNavigate("/profil/optionen");
  }}
>
  Profil löschen
</button>

          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleFeedbackClick();
            }}
          >
            Feedback geben
          </button>

          <button
            type="button"
            className="cancel"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closeMenu();
            }}
          >
            Abbrechen
          </button>
        </div>
      </>,
      document.body
    );

  const feedbackLayer =
    showFeedbackModal &&
    createPortal(
      <div
        className="modal-overlay"
        onClick={() => setShowFeedbackModal(false)}
      >
        <div
          className="modal-content"
          onClick={(event) => event.stopPropagation()}
        >
          <h3>Feedback geben</h3>

          <p>
            Über Feedback freuen wir uns sehr. Bitte nutze dafür das
            Kontaktformular im Footer von Edens Ernte.
          </p>

          <div className="modal-buttons">
            <button
              type="button"
              className="green-button"
              onClick={() => setShowFeedbackModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div
      className={`profile-action-wrapper ${className}`.trim()}
      ref={wrapperRef}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="profile-action-trigger"
        title="Profil-Optionen"
        aria-label="Profil-Optionen"
        onClick={handleToggleMenu}
      >
        ⋮
      </button>

      {menuLayer}
      {feedbackLayer}
    </div>
  );
}

export default ProfileAction;